import { css } from '@compiled/react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { JiraOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { CreateASiteText } from '../../../components/create-a-site-heading';
import { Headings } from '../../../components/headings';

export const LoggedInJiraNewUser = () => {
	// Fire the CONTROL-side Statsig exposure for the Defer Site Naming
	// Jira experiment (epic D1D6-47 / ATLAS-126008). This component is
	// the Jira N2N (logged-in new-user) site-naming page — rendering it
	// is the strongest "user saw the control experience" signal we can
	// get for the experiment cohort.
	//
	// Notes:
	//   - Scope already matches the experiment cohort (Jira + N2N +
	//     logged-in); no further filtering required.
	//   - The variant-side fire happens in `DeferSiteNamingGate` on the
	//     confirmation page's loading view. Together they form a
	//     symmetric pattern: each arm fires exactly when its own
	//     divergent UI is observed by the user.
	//   - The ref guard prevents Strict Mode / re-render double-fires.
	//   - Previous attempt (in bxpkit `SiteNameField`) targeted dead code
	//     — the Jira flow doesn't render that component. See D1D6-49
	//     audit-trail comment in Jira.
	const [, fireControlExposure] = useExperimentExposure(experiments.d1d6DeferSiteNaming);
	const hasFiredControlExposureRef = useRef(false);
	useEffect(() => {
		if (hasFiredControlExposureRef.current) return;
		hasFiredControlExposureRef.current = true;
		fireControlExposure();
	}, [fireControlExposure]);

	const subheading = (
		<FormattedMessage
			id="siteActivation.loggedInJira.newUser"
			defaultMessage="Sites are the shared space where people organize teams, work, and projects."
		/>
	);
	return (
		<FeatureExposed experimentKey="jfind-1565-sign-up-flow-overlay" group="Test">
			<JiraOverlayTemplate size="sm">
				<div css={containerCss}>
					<Headings main={<CreateASiteText />} sub={subheading} />
					<SiteActivation useContactExpandCohortLogic={true} />
				</div>
			</JiraOverlayTemplate>
		</FeatureExposed>
	);
};

const containerCss = css({
	display: 'flex',
	flexDirection: 'column',
});
